import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  GripVertical,
  Lock,
  LockOpen,
  Trash2,
  MoreHorizontal,
} from 'lucide-react';
import { DRAG_HANDLE_OFFSET_PX, WIDGET_GRID_COPY } from '../../constants';
import { useWidgetGridContext } from '../../context';

const MAX_VISIBLE_ACTIONS = 2;

/** Small icon-only button with hover background */
const IconButton = ({ icon: Icon, label, onClick = undefined, className = '' }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className={`inline-flex items-center justify-center w-7 h-7 rounded text-content-muted hover:bg-surface-hover transition-colors cursor-pointer ${className}`}
  >
    <Icon size={15} />
  </button>
);

IconButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};


/** Overflow dropdown for extra header buttons */
const OverflowMenu = ({ buttons, onSelect = undefined }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  const handleSelect = useCallback(
    (btn) => {
      setOpen(false);
      onSelect?.(btn.key);
    },
    [onSelect],
  );

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={menuRef} className="relative">
      <IconButton icon={MoreHorizontal} label={WIDGET_GRID_COPY.actions.options} onClick={handleToggle} />
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] bg-surface border border-edge rounded-lg shadow-lg py-1">
          {buttons.map((btn) => {
            const BtnIcon = btn.icon;
            return (
              <button
                key={btn.key}
                type="button"
                onClick={() => handleSelect(btn)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-content hover:bg-surface-hover transition-colors cursor-pointer text-left"
              >
                {BtnIcon && <BtnIcon size={14} />}
                {btn.tooltip || btn.key}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

OverflowMenu.propTypes = {
  buttons: PropTypes.array.isRequired,
  onSelect: PropTypes.func,
};


/**
 * Widget shell component driven by the backend contract.
 *
 * Header: title, optional description, action buttons from contract.
 * Shows first 2 buttons directly, overflows the rest into a dropdown menu.
 * Drag handle: visible on hover when isEditMode. If there is no title, description,
 * header buttons, or pin/delete actions, the handle floats in the corner so no empty
 * header row reserves vertical space; the card body uses even padding on all sides.
 *
 * @param {Array} [props.headerButtons] - Action buttons from config.card.header_buttons.
 *   Each item: { key, icon (Lucide component), tooltip, action }
 * @param {Function} [props.onAction] - Callback when a header button is clicked.
 */
const WidgetCardShell = ({
  title = '',
  description = '',
  headerButtons = null,
  onAction = undefined,
  onDelete = undefined,
  onPin = undefined,
  isPinned = false,
  bodyTopSlot = null,
  footerSlot = null,
  footerDisposition = 'horizontal',
  children = null,
}) => {
  const { isEditMode } = useWidgetGridContext();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const buttons = useMemo(() => headerButtons || [], [headerButtons]);
  const visibleButtons = useMemo(() => buttons.slice(0, MAX_VISIBLE_ACTIONS), [buttons]);
  const overflowButtons = useMemo(() => buttons.slice(MAX_VISIBLE_ACTIONS), [buttons]);

  const needsFullHeaderRow =
    Boolean(title) ||
    Boolean(description) ||
    buttons.length > 0 ||
    (isEditMode && (typeof onPin === 'function' || typeof onDelete === 'function'));

  const showFloatingDragOnly = isEditMode && !needsFullHeaderRow;

  const handleOverflowSelect = useCallback(
    (value) => {
      const btn = overflowButtons.find((b) => b.key === value);
      if (btn?.action) onAction?.(btn.action);
    },
    [overflowButtons, onAction],
  );

  const shellClass = [
    'relative flex flex-col h-full w-full overflow-x-visible bg-surface rounded-lg border border-edge transition-shadow duration-[30ms] ease-out',
    isHovered && isEditMode
      ? 'shadow-[2px_2px_8px_0px_rgba(0,0,0,0.06)] dark:shadow-[2px_2px_12px_0px_rgba(0,0,0,0.35)]'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const dragHandleOpacity = [
    'transition-opacity duration-[30ms] ease-out',
    isHovered ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none',
  ].join(' ');

  return (
    <div
      className={shellClass}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showFloatingDragOnly && (
        <div
          className={`absolute z-10 top-3 left-3 bg-surface p-1 rounded shadow-[2px_2px_8px_0px_rgba(0,0,0,0.06)] dark:shadow-[2px_2px_12px_0px_rgba(0,0,0,0.35)] flex items-center justify-center cursor-grab ${dragHandleOpacity}`}
        >
          <button
            type="button"
            aria-label={WIDGET_GRID_COPY.actions.dragWidget}
            className="widget-drag-handle inline-flex items-center justify-center w-7 h-7 rounded text-content-faint cursor-grab"
          >
            <GripVertical size={15} />
          </button>
        </div>
      )}

      {needsFullHeaderRow && (
        <div className="flex items-center gap-2 flex-shrink-0 px-6 relative min-h-[50px]">
          {isEditMode && (
            <div
              className={[
                'absolute top-1/2 -translate-y-1/2 bg-surface p-1 rounded shadow-[2px_2px_8px_0px_rgba(0,0,0,0.06)] dark:shadow-[2px_2px_12px_0px_rgba(0,0,0,0.35)] flex items-center justify-center cursor-grab',
                dragHandleOpacity,
              ].join(' ')}
              style={{ left: `-${DRAG_HANDLE_OFFSET_PX}px` }}
            >
              <button
                type="button"
                aria-label={WIDGET_GRID_COPY.actions.dragWidget}
                className="widget-drag-handle inline-flex items-center justify-center w-7 h-7 rounded text-content-faint cursor-grab"
              >
                <GripVertical size={15} />
              </button>
            </div>
          )}

          <div className="flex flex-col flex-1 min-w-0 gap-0.5">
            {title && (
              <span className="font-semibold text-xs text-content uppercase tracking-[0.48px] leading-none">
                {title}
              </span>
            )}
            {description && (
              <span className="font-medium text-xs text-content-muted leading-snug">
                {description}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {visibleButtons.map((btn) => {
              const BtnIcon = btn.icon;
              return BtnIcon ? (
                <IconButton
                  key={btn.key}
                  icon={BtnIcon}
                  label={btn.tooltip || btn.key}
                  onClick={() => onAction?.(btn.action)}
                />
              ) : null;
            })}

            {overflowButtons.length > 0 && (
              <OverflowMenu buttons={overflowButtons} onSelect={handleOverflowSelect} />
            )}

            {isEditMode && onPin && (
              <IconButton
                icon={isPinned ? Lock : LockOpen}
                label={isPinned ? WIDGET_GRID_COPY.actions.unpinWidget : WIDGET_GRID_COPY.actions.pinWidget}
                onClick={onPin}
              />
            )}

            {isEditMode && onDelete && (
              <IconButton
                icon={Trash2}
                label={WIDGET_GRID_COPY.actions.removeWidget}
                onClick={onDelete}
              />
            )}
          </div>
        </div>
      )}

      {bodyTopSlot && (
        <div
          className={
            needsFullHeaderRow
              ? 'flex-shrink-0 px-6'
              : 'flex-shrink-0 px-6 pt-6'
          }
        >
          {bodyTopSlot}
        </div>
      )}

      <div
        className={[
          'wg-card-body flex-1 min-h-0 overflow-x-hidden overflow-y-auto cursor-pointer',
          needsFullHeaderRow ? 'px-6 pb-6' : bodyTopSlot ? 'px-6 pb-6' : 'p-6',
        ].join(' ')}
      >
        {children}
      </div>

      {footerSlot && (
        <div
          className={[
            'flex gap-4 flex-shrink-0 px-6 pb-6 flex-wrap',
            footerDisposition === 'vertical' ? 'flex-col' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {footerSlot}
        </div>
      )}
    </div>
  );
};

WidgetCardShell.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  headerButtons: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      tooltip: PropTypes.string,
      action: PropTypes.object,
    }),
  ),
  onAction: PropTypes.func,
  onDelete: PropTypes.func,
  onPin: PropTypes.func,
  isPinned: PropTypes.bool,
  bodyTopSlot: PropTypes.node,
  footerSlot: PropTypes.node,
  footerDisposition: PropTypes.oneOf(['horizontal', 'vertical']),
  children: PropTypes.node,
};


export default WidgetCardShell;
